import { useAuth } from '@clerk/clerk-react';
import { useCallback } from 'react';
import { z } from 'zod';
import { isClientAuthDebug, clientAuthLog } from './debug';

import { impersonateWriteHeaders } from './impersonate-write-mode';
import { routeSurface } from './route-surface';
import { describeBlocked } from '@shared/external-writes/describe';
import { useExternalWritesStore } from '../view-system/debug/external-writes-store';
import { ExternalWriteBlockedProblemSchema } from '@shared/external-writes/wire';
import { WriteRefusedError } from './apiProblemError';
import { notifyError } from './errors';
import { extractUserMessage } from '@shared/error-message';
import { safeParseResponse } from '@shared/parse-json';
import { ErrorCodeBodySchema, errorBodyCode } from '@shared/schemas/error-envelope';

const READ_ONLY_IMPERSONATION_MESSAGE = 'Read-only impersonation — writes are disabled';

/** Announces a refusal through `notifyError` (unless the caller opted out) and returns it for the throw. */
function refusal(err: WriteRefusedError, announce: boolean): WriteRefusedError {
  if (announce) notifyError(err, 'write-refused');
  return err;
}

// Dev-only: LCP entry type (not yet in standard lib)
interface PerformanceLCPEntry extends PerformanceEntry {
  element?: Element;
}

// Dev-only: method/status enrichment map for PerformanceObserver
const recentRequests = new Map<string, { method: string; status: number }>();

if (import.meta.env.DEV) {
  // Dynamic import to keep perf-debug-events out of prod bundle
  void import('./perf-debug-events').then(({ emitPerfEntry, emitLongTask, updatePageMetrics }) => {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'longtask') {
          emitLongTask({ startMs: entry.startTime, durationMs: entry.duration });
          continue;
        }
        if (entry.entryType === 'largest-contentful-paint') {
          updatePageMetrics({
            lcpMs: entry.startTime,
            lcpElement: (entry as PerformanceLCPEntry).element?.tagName,
          });
          continue;
        }
        const resEntry = entry as PerformanceResourceTiming;
        if (!resEntry.name.includes('/api/')) continue;
        if (!resEntry.serverTiming?.length) continue;

        const url = new URL(resEntry.name);
        const clientReqId = resEntry.serverTiming.find(m => m.name === 'reqid')?.description;
        const enrichment = recentRequests.get(url.pathname);

        emitPerfEntry({
          timestamp: Date.now(),
          clientReqId,
          path: url.pathname,
          method: enrichment?.method ?? 'GET',
          status: enrichment?.status ?? 0,
          serverPhases: resEntry.serverTiming
            .filter(st => st.name !== 'reqid')
            .map(st => ({
              name: st.name,
              durationMs: st.duration,
              description: st.description || undefined,
            })),
          networkTiming: {
            dns: resEntry.domainLookupEnd - resEntry.domainLookupStart,
            tcp: resEntry.connectEnd - resEntry.connectStart,
            ttfb: resEntry.responseStart - resEntry.requestStart,
            download: resEntry.responseEnd - resEntry.responseStart,
          },
          totalNetworkMs: resEntry.duration,
        });
      }
    });
    observer.observe({ type: 'resource', buffered: true });
    observer.observe({ type: 'longtask', buffered: true });
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  });
}

/**
 * Simple wrapper that attaches Clerk session token and cookies
 * to every API request so the server can authenticate reliably.
 */

// Per-call abort timeout in ms (default 15_000). Bump for endpoints that legitimately run longer.
// `announceRefusal: false` skips the policy-refusal toast (still rejects); only `backgroundWrite` sets it.
export type ApiFetchInit = RequestInit & { timeoutMs?: number; announceRefusal?: false };

/**
 * One signal that aborts when EITHER input does (Codex round-2 **#6**, plan
 * amendment **A21**).
 *
 * The bug this replaces: `apiFetch` used
 * `signal: fetchInit.signal || controller.signal` — an either/or. A caller
 * that supplied a signal (React Query's, for request cancellation) silently
 * REPLACED the 15 s timeout controller; a caller that didn't got no
 * cancellation at all. Trading one leak for the other is why
 * `plays/hooks.ts` declined to thread React Query's signal and
 * `useFlowPlays` kept firing one uncancellable request — plus up to three
 * Terra-Signals count aggregates server-side — per partial search value.
 *
 * `AbortSignal.any` is the platform primitive for exactly this and IS
 * available in this app's browser targets and in the vitest/jsdom
 * environment (asserted directly by
 * `lib/__tests__/apiFetch.signalComposition.test.tsx`, so the assumption
 * fails loudly rather than silently degrading). The manual fallback exists
 * for older browsers only: it forwards the first abort onto a fresh
 * controller, which is behaviourally equivalent for our use (we only ever
 * need "aborted, with a reason", never the full spec's dependent-signal
 * semantics).
 */
function anySignal(signals: AbortSignal[]): AbortSignal {
  if (typeof AbortSignal.any === 'function') return AbortSignal.any(signals);

  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    // `{ once: true }` plus the fact that an AbortController can only abort
    // once makes the duplicate-abort case a no-op; no teardown needed, since
    // every signal here dies with the request.
    signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}

/**
 * The machine code in a failed response's `{ error: '<code>' }`, `{ error: { code, message } }` or
 * `problem()` `{ code }` body, read off a clone so the caller still gets an unconsumed response.
 * A legacy `error` wins over `code`. `undefined` for a non-JSON body, an empty body, or a body
 * with no code.
 */
export async function errorCodeOf(res: Response): Promise<string | undefined> {
  const parsed = await safeParseResponse(ErrorCodeBodySchema, res.clone());
  return parsed.success ? errorBodyCode(parsed.data) : undefined;
}

/**
 * Extracts the server's own explanatory message from a `!res.ok` response
 * body, falling back to `fallback` when the body doesn't carry one — the
 * shared version of a pattern duplicated in `useSetCronOverride.ts` and
 * `useAdminDbosRunNow.ts`.
 *
 * Never throws: a non-JSON body, an empty body, or a body whose stream was
 * already consumed by the caller (e.g. via `res.clone()` upstream) all
 * resolve to `fallback`.
 *
 * Precedence is the single chain in `./errorMessage` (`extractUserMessage`),
 * the same one `fetchJson` uses for `ApiProblemError.userMessage`: a string
 * `error` is a machine code and is never surfaced, a blank or non-string value
 * is skipped, and a `message` that reads as a raw HTTP diagnostic is dropped.
 */
export async function errorMessageFromResponse(res: Response, fallback: string): Promise<string> {
  const parsed = await safeParseResponse(z.unknown(), res);
  return (parsed.success ? extractUserMessage(parsed.data) : undefined) ?? fallback;
}

/**
 * `apiFetch` resolves with the Response for every status, `!res.ok` included,
 * EXCEPT a policy refusal: a 403 `read_only_impersonation` or a 423 whose body
 * parses as `ExternalWriteBlockedProblemSchema`. It announces that refusal
 * itself through `notifyError` (one toast) and rejects with `WriteRefusedError`,
 * so the rep sees it however the caller handles the rejection. Callers need not
 * toast it and must not toast it again; the global MutationCache, QueryCache and
 * unhandled-rejection handlers skip it.
 */
/** True once this page load has started the navigation to the not-allowed screen. */
let notAllowedRedirectStarted = false;

/** Test-only: forget that this page load already navigated. */
export function __resetNotAllowedRedirectForTest(): void {
  notAllowedRedirectStarted = false;
}

export function useApi() {
  const { getToken } = useAuth();

  const apiFetch = useCallback(async (input: RequestInfo | URL, init: ApiFetchInit = {}) => {
    const { timeoutMs: timeoutOverride, announceRefusal = true, ...fetchInit } = init;
    const isDebug = isClientAuthDebug();
    const startedAt = Date.now();
    const rawToken = await getToken().catch(() => null);
    const effectiveToken = rawToken && rawToken.length > 0 ? rawToken : null;

    const headers = new Headers(fetchInit.headers || {});
    if (effectiveToken) headers.set('Authorization', `Bearer ${effectiveToken}`);
    // Write-mode header for impersonation sessions
    for (const [name, value] of Object.entries(impersonateWriteHeaders())) headers.set(name, value);
    // Attach a client request id for correlation
    const clientReqId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    headers.set('x-client-request-id', clientReqId);
    // Stamp the SPA route the rep is on so server-emitted analytics events
    // (queue add / play assigned / contact enriched …) can attribute to the
    // page of origin — the server otherwise only sees the API URL. Read +
    // validated server-side by readRequestOrigin (src/http/request-origin.ts).
    try {
      const pathname = window.location.pathname;
      headers.set('x-flow-path', pathname);
      headers.set('x-flow-surface', routeSurface(pathname).surface);
    } catch { /* non-browser / no location — skip */ }
    const controller = new AbortController();
    const timeoutMs = timeoutOverride ?? 15_000;
    const timeout = setTimeout(() => {
      try { controller.abort(); } catch {}
    }, timeoutMs);
    const opts: RequestInit = {
      credentials: 'include',
      ...fetchInit,
      headers,
      // Codex #6 — COMPOSED, not either/or: the caller's signal (React
      // Query's, for cancelling a superseded request) and this call's
      // timeout must both be able to abort the fetch. `fetchInit.signal`
      // used to win outright and drop the timeout.
      signal: fetchInit.signal
        ? anySignal([fetchInit.signal, controller.signal])
        : controller.signal,
    };
    const url = typeof input === 'string' ? input : (input as Request)?.url || 'unknown';
    if (isDebug) {
      const cookieNames = (() => {
        try { return (document.cookie || '').split(';').map(s => s.trim().split('=')[0]).filter(Boolean); } catch { return []; }
      })();
      clientAuthLog('api_request', { url, method: (opts as any)?.method || 'GET', hasToken: !!effectiveToken, clientReqId, cookieNames });
    }
    let res: Response;
    try {
      res = await fetch(input, opts);
    } finally {
      try { clearTimeout(timeout); } catch {}
    }
    if (import.meta.env.DEV) {
      const reqUrl = typeof input === 'string' ? input : (input as Request)?.url || '';
      try {
        const parsed = new URL(reqUrl, window.location.origin);
        recentRequests.set(parsed.pathname, { method: (fetchInit.method || 'GET').toUpperCase(), status: res.status });
        setTimeout(() => recentRequests.delete(parsed.pathname), 5000);
      } catch {}
    }
    if (isDebug) {
      clientAuthLog('api_response', { url, status: res.status, durationMs: Date.now() - startedAt, clientReqId });
      if (res.status === 401 || res.status === 403 || res.status === 409) {
        const hasSessionCookie = (() => {
          try { return document.cookie.includes('__session='); } catch { return false; }
        })();
        clientAuthLog('api_auth_error', { url, status: res.status, hasToken: !!effectiveToken, hasSessionCookie, clientReqId });
      }
    }
    // Handle first-login mapping failures gracefully: bounce to sign-in with reason.
    //
    // MUST be a real navigation (real pathname + search), not a hash
    // mutation: under BrowserRouter, `location.hash = ...` never changes
    // pathname/search, so react-router never sees a route change — the old
    // `u.hash = '/sign-in?reason=...'` was a same-document no-op that left
    // the user parked on the 409ing authenticated shell, where every query
    // (retries included) kept toasting. `/sign-in` is public
    // (RequireSignedOut gates it) and special-cases this exact `reason` to
    // admit a Clerk-ready-but-unmapped principal instead of bouncing it back
    // to `/` — see RequireSignedOut.tsx for why that exception exists (it's
    // what breaks the redirect loop this reason code would otherwise cause).
    if (res.status === 409) {
      if ((await errorCodeOf(res)) === 'slack_user_not_found' && typeof window !== 'undefined') {
        window.location.replace('/sign-in?reason=slack_user_not_found');
      }
    }
    if (res.status === 403) {
      const code = await errorCodeOf(res);
      // The server does not admit this email (outside ALLOWED_EMAIL_DOMAINS,
      // with no `external_sign_in` grant, or one since revoked). Same real
      // navigation as the 409 above, and the same RequireSignedOut exception
      // stops it looping.
      // Navigate once per page load, and never from the sign-in page itself:
      // several queries 403 together, and the sign-in page can fire one too.
      if (
        code === 'forbidden_domain'
        && typeof window !== 'undefined'
        && !notAllowedRedirectStarted
        && !window.location.pathname.startsWith('/sign-in')
      ) {
        notAllowedRedirectStarted = true;
        window.location.replace('/sign-in?reason=not_allowed');
      }
      if (code === 'read_only_impersonation') {
        throw refusal(new WriteRefusedError({ refusal: 'read_only_impersonation', userMessage: READ_ONLY_IMPERSONATION_MESSAGE }), announceRefusal);
      }
    }
    if (res.status === 423) {
      const problem = await safeParseResponse(ExternalWriteBlockedProblemSchema, res.clone());
      if (problem.success) {
        const known = useExternalWritesStore.getState().state;
        throw refusal(new WriteRefusedError({
          refusal: 'external_write_blocked',
          problem: problem.data,
          userMessage: describeBlocked(problem.data, known === 'unknown' ? null : known, new Date()),
        }), announceRefusal);
      }
    }
    return res;
  }, [getToken]);

  return { apiFetch };
}
